import React from 'react';
import { StyledInputContainer, StyledInput, StyledLabel, StyledUnderline, StyledMessage } from './Input.styles.es.js';
import { InputDefaultProps } from './Input.types.es.js';
import { InputIcons } from './InputIcon.es.js';

/**
 * Renders a <Input /> component
 * @param {string} className - an optional param - add class on the element
 * @param {string} id - `id` html attribute that will be passed to the `input` / `textarea`.
 * @param {string} label - Input label that is visible above the input value.
 * @param {string} value - set the value on the element
 * @param {boolean} disabled - Sets disabled state.
 * @param {boolean} autoFocus - an optional param - Sets `autoFocus` attribute.
 * @param {boolean} readOnly - Sets `readOnly` attribute on the `input` / `textarea` element.
 * @param {string} role - an optional param - set the role attribute
 * @param {number} tabIndex - set the tabIndex
 * @param {string} as - render as 'input' | 'textarea' | 'div';
 * @param {string} type - type` html attribute that will be passed to `input`
 * @param {number} height - Set it to make the input higher than usual
 * @param {number} maxLength - an optional param - The maximum number of characters allowed in the Input element.
 * @param {React.ReactNode[]} icons - An array of React Node icons
 * @param {boolean} valid - Toggles valid state styling
 * @param {boolean} required - Adds default `* Required` hint.
 * @param {string} hint - an optional param - Text that displays below the input not that an error or  or a required message will take precedence over the hint
 * @param {string} error - an optional param - If error string is passed it it will add all error styling to the Input.
 * @param {string} placeholder - an optional param - Will be shown instead of value when value is empty.
 * @param {string} defaultValue - an optional param - pass this instead of `value`.
 * @param {boolean} active - forces the input to be highlighted as focused.
 * @param {string} autoComplete - an optional param -  used for accessible form completion
 * @param {React.RefObject<HTMLInputElement>} inputRef - an optional param - set the reference of the input
 * @param {void} onFocus - an optional param - attach vent listener on the input
 * @param {void} onBlur - an optional param - attach vent listener on the input
 * @param {void} onChange - an optional param - attach vent listener on the input
 * @param {void} onClick - an optional param - attach vent listener on the input
 * @param {void} onKeyPress - an optional param - attach vent listener on the input
 * @param {void} onKeyDown - an optional param - attach vent listener on the input
 * @param {void} onKeyUp - an optional param - attach vent listener on the input
 **/

var Input = function Input(props) {
  var footerMessageText = function footerMessageText() {
    var required = props.required,
        error = props.error,
        hint = props.hint,
        value = props.value;
    var requiredMessage = required && !value ? '* Required' : undefined; // set precedent of footer messages here

    return error || requiredMessage || hint;
  };

  var handleBlur = function handleBlur(e) {
    if (props.onBlur) {
      props.onBlur(e.target.value, e);
    }
  };

  var handleChange = function handleChange(e) {
    if (props.onChange) {
      props.onChange(e.target.value, e);
    }
  };

  var active = props.active,
      as = props.as,
      defaultValue = props.defaultValue,
      disabled = props.disabled,
      autoFocus = props.autoFocus,
      error = props.error,
      height = props.height,
      maxLength = props.maxLength,
      icons = props.icons,
      id = props.id,
      label = props.label,
      onFocus = props.onFocus,
      onClick = props.onClick,
      onKeyDown = props.onKeyDown,
      onKeyPress = props.onKeyPress,
      onKeyUp = props.onKeyUp,
      placeholder = props.placeholder,
      readOnly = props.readOnly,
      required = props.required,
      type = props.type,
      valid = props.valid,
      _props$value = props.value,
      value = _props$value === void 0 ? '' : _props$value,
      inputRef = props.inputRef,
      role = props.role,
      tabIndex = props.tabIndex,
      ariaLabel = props['aria-label'],
      ariaHasPopup = props['aria-haspopup'],
      ariaExpanded = props['aria-expanded'],
      autoComplete = props.autoComplete,
      className = props.className;
  var message = footerMessageText();
  var hasError = !disabled && Boolean(error);
  var inputProps = {
    id: id,
    'data-testid': "".concat(id, " Box"),
    ref: inputRef,
    active: active,
    value: as === 'div' ? undefined : value,
    hasValue: !!value,
    hasPlaceholder: !!placeholder,
    defaultValue: defaultValue,
    placeholder: placeholder,
    readOnly: readOnly,
    disabled: disabled,
    autoFocus: autoFocus,
    hasError: hasError,
    height: height,
    maxLength: maxLength,
    role: role,
    'aria-labelledby': label ? "".concat(id, "__label") : undefined,
    'aria-describedby': message ? "".concat(id, "__message") : undefined,
    'aria-haspopup': ariaHasPopup,
    'aria-expanded': ariaExpanded,
    autoComplete: autoComplete,
    tabIndex: tabIndex,
    onFocus: onFocus,
    onBlur: handleBlur,
    onChange: handleChange,
    onKeyDown: onKeyDown,
    onKeyPress: onKeyPress,
    onKeyUp: onKeyUp
  };
  return /*#__PURE__*/React.createElement(StyledInputContainer, {
    onClick: disabled ? undefined : onClick,
    className: className,
    "data-testid": id
  }, as === 'input' ? /*#__PURE__*/React.createElement(StyledInput, Object.assign({}, inputProps, {
    as: as,
    type: type
  })) : /*#__PURE__*/React.createElement(StyledInput, Object.assign({}, inputProps, {
    as: as
  }), as === 'div' ? value : undefined), /*#__PURE__*/React.createElement(StyledLabel, {
    id: "".concat(id, "__label"),
    hasError: hasError,
    htmlFor: id,
    "aria-label": ariaLabel
  }, label), /*#__PURE__*/React.createElement(InputIcons, {
    icons: icons,
    hasError: hasError,
    valid: valid,
    disabled: disabled
  }), /*#__PURE__*/React.createElement(StyledUnderline, {
    showUnderline: (hasError || valid) && !disabled,
    hasError: hasError,
    "data-testid": "input_underline"
  }), /*#__PURE__*/React.createElement(StyledMessage, {
    id: "".concat(id, "__message"),
    required: required,
    hasError: hasError,
    hasMessage: Boolean(message),
    "data-testid": "required_message"
  }, message));
};
Input.defaultProps = InputDefaultProps;

export { Input };
